import { API_CONFIG, API_ENDPOINTS, getHeadersWithOrg, apiCall, authFetch } from './client';
import i18n from '../../i18n';
import {
  ErrorResponse,
  McpToolConfigResponse,
  UpdateMcpToolConfigRequest,
} from '../../types';

/**
 * Fetch the MCP client configuration for a site.
 * Returns a JSON object suitable for use in MCP client config files (e.g., Cursor).
 */
export async function fetchMcpConfig(
  siteId: string,
  orgId: string,
  apiKeyId?: string,
): Promise<Record<string, unknown>> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.MCP_CONFIG(siteId)}`;
  if (apiKeyId) {
    url += `?apiKeyId=${encodeURIComponent(apiKeyId)}`;
  }

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to fetch MCP config: ${response.status}`);
    }

    const data = await response.json();
    return data.config;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.mcp.configFetchFailed'));
  }
}

/**
 * Fetch the MCP tool configuration (tool name & description) for a site.
 */
export async function getMcpToolConfig(siteId: string): Promise<McpToolConfigResponse> {
  return apiCall<McpToolConfigResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.MCP_TOOL_CONFIG(siteId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Create or update the MCP tool configuration for a site.
 */
export async function updateMcpToolConfig(
  siteId: string,
  config: UpdateMcpToolConfigRequest,
): Promise<McpToolConfigResponse> {
  return apiCall<McpToolConfigResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.MCP_TOOL_CONFIG(siteId)}`,
    {
      method: 'PUT',
      headers: getHeadersWithOrg(),
      body: JSON.stringify(config),
    },
  );
}
